import React from "react";
import PhoneInput, {
  isValidPhoneNumber,
  parsePhoneNumber,
} from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import "react-phone-number-input/style.css";
import { Box, LabelStyled } from "../../styled/Elements";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import ValidationErrorText from "./ValidationErrorText";

const PhoneInputField = ({
  name,
  control,
  label,
  disabled,
  className,
  staric,
  classNameLabel,
  errorDisplay,
  errorMessage = "",
  validationRules,
  register,
  ...rest
}) => {
  const { t, i18n } = useTranslation();

  const langDir = i18n.dir()

  // Custom country selector component that forces Saudi Arabia and shows the flag
  const CountrySelect = ({ value, onChange, options, ...rest }) => {
    // Always return the Saudi Arabia flag component
    return (
      <div className="PhoneInputCountryIcon mb-1">
        <flags.SA/>
      </div>
    );
  };

  const defaultValidationRules = {
    validate: {
      validNumber: (value) => {
        if (!value) return true;

        try {
          const phoneNumber = parsePhoneNumber(value);

          // Check if the number is valid for Saudi Arabia specifically
          if (!isValidPhoneNumber(value, "SA")) {
            return "invalidPhoneNumber";
          }

          if (!phoneNumber.isValid()) {
            return "invalidPhoneNumber";
          }

          // Get the national number without country code
          const nationalNumber = phoneNumber.nationalNumber;

          if (nationalNumber.length < 4) {
            return "phoneNumberTooShort";
          }
          if (nationalNumber.length > 15) {
            return "phoneNumberTooLong";
          }

          return true;
        } catch (error) {
          return "invalidPhoneNumber";
        }
      },
    },
  };

  const combinedValidationRules = {
    ...defaultValidationRules,
    ...validationRules,
  };

  return (
    <PhoneInputWrapper className={className} langDir={langDir} {...rest}>
      <div className="formEleContainer">
        {label && (
          <LabelStyled
            className={classNameLabel}
            // size="16px"
            color="var(--darkGrey)"
          >
            {t(label)}
            {staric && <span className="staric">*</span>}
          </LabelStyled>
        )}
        <Box className="position-relative w-100">
          <Controller
            name={name}
            control={control}
            rules={combinedValidationRules}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <>
                <PhoneInput
                  international
                  defaultCountry="SA"
                  country="SA"
                  value={value}
                  onChange={(newValue) => {
                    // Always ensure the country code is Saudi Arabia
                    if (newValue) {
                      // Force the country code to be Saudi Arabia's (+966)
                      try {
                        const phoneNumber = parsePhoneNumber(newValue);
                        if (phoneNumber && phoneNumber.country !== 'SA') {
                          // Keep only the national number and add SA country code
                          onChange(`+966${phoneNumber.nationalNumber}`);
                          return;
                        }
                      } catch (e) {
                        // If parsing fails, just proceed with the value
                      }
                    }
                    onChange(newValue);
                  }}
                  disabled={disabled}
                  flags={flags}
                  className="phone-input validated-input PhoneInputCountry px-2 px-sm-3"
                  countrySelectComponent={CountrySelect}
                  countryCallingCodeEditable={false}
                />
                {error && (
                  <>
                    {error?.type === "required" && (
                      <ValidationErrorText
                        text={
                          t(error?.message) ||
                          `${t(label || name)} ${t("isRequired")}.`
                        }
                        extraClass={errorDisplay}
                      />
                    )}
                    {error?.type === "minLength" && (
                      <ValidationErrorText
                        text={t(error?.message)}
                        extraClass={errorDisplay}
                      />
                    )}

                    {error?.type === "maxLength" && (
                      <ValidationErrorText
                        text={t(error?.message)}
                        extraClass={errorDisplay}
                      />
                    )}

                    {error?.type === "pattern" && (
                      <ValidationErrorText
                        text={t(error?.message || errorMessage)}
                        extraClass={errorDisplay}
                      />
                    )}
                    {error?.type === "validNumber" && (
                      <ValidationErrorText
                        text={t(error?.message || errorMessage)}
                        extraClass={errorDisplay}
                      />
                    )}
                  </>
                )}
              </>
            )}
          />
        </Box>
      </div>
    </PhoneInputWrapper>
  );
};

export default PhoneInputField;

export const PhoneInputWrapper = styled.div`
  width: 100%;
  padding: 0px 0px 0px 0px;
  width: 100%;
  .formEleContainer {
    display: flex;
    padding: 0px 0px 0px 0px;
    margin: 10px 0 10px 0;
    align-items: center;
    gap: 5px;
    & > label {
      min-width: 160px;

      body.rtl & {
        font-family: var(--arSemiBold);
      }

      &.w-auto {
        min-width: auto;
      }
    }
  }
  &.combined {
    border-bottom: none;
    padding: 8px 25px 8px 25px;
    .formEleContainer {
      display: flex;
      padding: 0;
      margin: 0;
    }
  }
  &.flex-wrap {
    .formEleContainer {
      flex-wrap: wrap;
      label {
        width: 100%;
      }
    }
  }
  .form-floating {
    width: 100%;
  }
  input[type="text"] {
    width: 100%;
  }

  .phone-input {
    height: 48px;
    box-shadow: none;
    outline: none;
    border-radius: 10px;
    border: 1px solid var(--border);
    background-color: ${({ bg }) => (bg ? bg : "var(--bg)")};
    svg {
      vertical-align: unset !important;
    }
    .PhoneInputInput {
      height: 100%;
      border: none;
      outline: none;
      background: none;
      color: var(--text);
      &::placeholder {
        color: var(--textPlaceholder2);
      }
    }
    /* Style to make the country selector look normal but non-interactive */
    .PhoneInputCountry {
      pointer-events: none;
    }
    body.rtl & {
      /* &.PhoneInput {
        flex-direction: row-reverse;
      } */
      /* .PhoneInputCountry {
        flex-direction: row-reverse;
      } */
        .PhoneInputInput{
          text-align: right;
        }
    }
    /* Hide dropdown arrow or make it appear non-clickable */
    .PhoneInputCountrySelectArrow {
      opacity: 0;
    }
  }
  .form-control:focus {
    box-shadow: none;
    outline: none;
    border: 1px solid var(--border);
  }
  @media (max-width: 767px) {
    .formEleContainer {
      flex-wrap: wrap;
      gap: 3px;
      margin: 0;
      padding: 6px 0px 6px 0px;
      label {
        width: 100%;
      }
    }
  }
`;


// import React from "react";
// import PhoneInput, {
//   isValidPhoneNumber,
//   parsePhoneNumber,
// } from "react-phone-number-input";
// import flags from "react-phone-number-input/flags";
// import "react-phone-number-input/style.css";
// import { Box, LabelStyled } from "../../styled/Elements";
// import styled from "styled-components";
// import { useTranslation } from "react-i18next";
// import { useForm, Controller } from "react-hook-form";
// import ValidationErrorText from "./ValidationErrorText";

// const PhoneInputField = ({
//   name,
//   control,
//   label,
//   disabled,
//   className,
//   staric,
//   classNameLabel,
//   errorDisplay,
//   errorMessage = "",
//   validationRules,
//   register,
//   ...rest
// }) => {
//   const { t } = useTranslation();

//   const defaultValidationRules = {
//     validate: {
//       validNumber: (value) => {
//         if (!value) return true;

//         try {
//           const phoneNumber = parsePhoneNumber(value);

//           // Check if the number is valid for any country
//           if (!isValidPhoneNumber(value)) {
//             return "invalidPhoneNumber";
//           }

//           if (!phoneNumber.isValid()) {
//             return "invalidPhoneNumber";
//           }

//           // Get the national number without country code
//           const nationalNumber = phoneNumber.nationalNumber;

//           if (nationalNumber.length < 4) {
//             return "phoneNumberTooShort";
//           }
//           if (nationalNumber.length > 15) {
//             return "phoneNumberTooLong";
//           }

//           return true;
//         } catch (error) {
//           return "invalidPhoneNumber";
//         }
//       },
//     },
//   };

//   const combinedValidationRules = {
//     ...defaultValidationRules,
//     ...validationRules,
//   };

//   return (
//     <PhoneInputWrapper className={className} {...rest}>
//       <div className="formEleContainer">
//         {label && (
//           <LabelStyled
//             className={classNameLabel}
//             size="16px"
//             color="var(--darkGrey)"
//           >
//             {t(label)}
//             {staric && <span className="staric">*</span>}
//           </LabelStyled>
//         )}
//         <Box className="position-relative w-100">
//           <Controller
//             name={name}
//             control={control}
//             rules={combinedValidationRules}
//             render={({ field: { onChange, value }, fieldState: { error } }) => (
//               <>
//                 <PhoneInput
//                   international
//                   defaultCountry="SA"
//                   value={value}
//                   onChange={onChange}
//                   disabled={disabled}
//                   flags={flags}
//                   className="phone-input px-2 px-sm-3"
//                 />
//                 {error && (
//                   <>
//                     {error?.type === "required" && (
//                       <ValidationErrorText
//                         text={
//                           t(error?.message) ||
//                           `${t(label || name)} ${t("isRequired")}.`
//                         }
//                         extraClass={errorDisplay}
//                       />
//                     )}
//                     {error?.type === "minLength" && (
//                       <ValidationErrorText
//                         text={t(error?.message)}
//                         extraClass={errorDisplay}
//                       />
//                     )}

//                     {error?.type === "maxLength" && (
//                       <ValidationErrorText
//                         text={t(error?.message)}
//                         extraClass={errorDisplay}
//                       />
//                     )}

//                     {error?.type === "pattern" && (
//                       <ValidationErrorText
//                         text={t(error?.message || errorMessage)}
//                         extraClass={errorDisplay}
//                       />
//                     )}
//                     {error?.type === "validNumber" && (
//                       <ValidationErrorText
//                         text={t(error?.message || errorMessage)}
//                         extraClass={errorDisplay}
//                       />
//                     )}
//                   </>
//                 )}
//               </>
//             )}
//           />
//         </Box>
//       </div>
//     </PhoneInputWrapper>
//   );
// };

// export default PhoneInputField;

// export const PhoneInputWrapper = styled.div`
//   width: 100%;
//   padding: 0px 0px 0px 0px;
//   width: 100%;
//   .formEleContainer {
//     display: flex;
//     padding: 0px 0px 0px 0px;
//     margin: 10px 0 10px 0;
//     align-items: center;
//     gap: 5px;
//     & > label {
//       min-width: 160px;

//       body.rtl & {
//         font-family: var(--arRegular);
//       }

//       &.w-auto {
//         min-width: auto;
//       }
//     }
//   }
//   &.combined {
//     border-bottom: none;
//     padding: 8px 25px 8px 25px;
//     .formEleContainer {
//       display: flex;
//       padding: 0;
//       margin: 0;
//     }
//   }
//   &.flex-wrap {
//     .formEleContainer {
//       flex-wrap: wrap;
//       label {
//         width: 100%;
//       }
//     }
//   }
//   .form-floating {
//     width: 100%;
//   }
//   input[type="text"] {
//     width: 100%;
//   }

//   .phone-input {
//     height: 48px;
//     box-shadow: none;
//     outline: none;
//     border-radius: 10px;
//     border: 1px solid var(--border);
//     background-color: ${({ bg }) => (bg ? bg : "var(--bg)")};
//     svg {
//       vertical-align: unset !important;
//     }
//     .PhoneInputInput {
//       height: 100%;
//       border: none;
//       outline: none;
//       background: none;
//       color: var(--text);
//       &::placeholder {
//         color: var(--textPlaceholder2);
//       }
//     }
//     body.rtl & {
//       &.PhoneInput {
//         flex-direction: row-reverse;
//       }
//       .PhoneInputCountry {
//         flex-direction: row-reverse;
//       }
//     }
//     /* .PhoneInputCountrySelectArrow {
//       margin: 0;
//     } */
//   }
//   .form-control:focus {
//     box-shadow: none;
//     outline: none;
//     border: 1px solid var(--border);
//   }
//   @media (max-width: 767px) {
//     .formEleContainer {
//       flex-wrap: wrap;
//       gap: 3px;
//       margin: 0;
//       padding: 6px 0px 6px 0px;
//       label {
//         width: 100%;
//       }
//     }
//   }
// `;
